import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { authClient } from '@/lib/auth'
import { apiClient } from '@/lib/api'

type SessionUser = {
  id: string
  email: string
  name: string
  image?: string | null
}

interface AuthContextValue {
  user: SessionUser | null
  loading: boolean
  isSuperAdmin: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function getStoredImpersonatedUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem('qopr_impersonated_user')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Only treat as impersonating if the impersonated token is also present
    if (!localStorage.getItem('qopr_impersonated_session_token')) return null
    return { id: parsed.id, email: parsed.email, name: parsed.name, image: null }
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { data: session, isPending } = authClient.useSession()
  const [isSuperAdminState, setIsSuperAdminState] = useState(false)

  const sessionUser = session?.user
    ? { id: session.user.id, email: session.user.email, name: session.user.name, image: session.user.image }
    : null

  // While impersonating, expose the impersonated user as the current user
  // so route guards, page headers, and queries all run as that user
  const impersonatedUser = getStoredImpersonatedUser()
  const user = impersonatedUser ?? sessionUser

  // If impersonating, treat loading as false — we have the stored user already
  const loading = impersonatedUser ? false : isPending

  useEffect(() => {
    if (!sessionUser) {
      setIsSuperAdminState(false)
      return
    }
    apiClient.get<{ data: { isSuperAdmin: boolean } }>('/admin/check')
      .then((res) => setIsSuperAdminState(res.data.data.isSuperAdmin))
      .catch(() => setIsSuperAdminState(false))
  }, [sessionUser?.id])

  // isSuperAdmin is false while impersonating — you're acting as the target user
  const isSuperAdmin = impersonatedUser ? false : isSuperAdminState

  async function login(email: string, password: string): Promise<void> {
    const result = await authClient.signIn.email({ email, password })
    if (result.error) throw new Error(result.error.message ?? 'Login failed')
  }

  async function register(email: string, password: string, name: string): Promise<void> {
    const result = await authClient.signUp.email({ email, password, name })
    if (result.error) throw new Error(result.error.message ?? 'Registration failed')
  }

  function logout(): void {
    authClient.signOut().finally(() => navigate('/login'))
  }

  return (
    <AuthContext.Provider value={{ user, loading, isSuperAdmin, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
